import { useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

import { AgentCompositeSection } from "../components/production/AgentCompositeSection";
import { AvatarSection } from "../components/production/AvatarSection";
import { WowShotsSection } from "../components/production/WowShotsSection";
import { WalkthroughAudioSection } from "../components/production/WalkthroughAudioSection";
import { ClosingFrameSection } from "../components/production/ClosingFrameSection";
import { ClipPickerDialog } from "../components/production/ClipPickerDialog";

export default function ProductionView({ session, onUpdate }) {
  const [generating, setGenerating] = useState({});
  const [clipPickerTarget, setClipPickerTarget] = useState(null);
  const [availableClips, setAvailableClips] = useState([]);
  const [loadingClips, setLoadingClips] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);

  const production = session?.production || {};
  const storyboard = session?.storyboard || {};
  const images = session?.property_image_urls || [];

  const agentComposite = production.agent_composite || {};
  const introData = production.avatar_intro || {};
  const ctaData = production.avatar_cta || {};
  const introAudio = production.audio_intro || {};
  const ctaAudio = production.audio_cta || {};
  const walkthroughAudio = production.audio_walkthrough || {};
  const wowShots = production.wow_shots || [];
  const compositeReady = agentComposite.status === 'ready';

  const refreshSession = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${session.id}`);
      onUpdate(res.data);
    } catch {}
  }, [session.id, onUpdate]);

  const handleGenerate = async (endpoint, key, params = {}) => {
    setGenerating(prev => ({ ...prev, [key]: true }));
    try {
      const url = `/sessions/${session.id}/${endpoint}`;
      const query = new URLSearchParams(params).toString();
      await api.post(`${url}${query ? '?' + query : ''}`);
      toast.success(`${key} generation started`);
      const poll = setInterval(async () => {
        try {
          const updated = await api.get(`/sessions/${session.id}/production`);
          const prod = updated.data;
          const relevantKey = key.includes('composite') ? `avatar_${params.segment || 'intro'}` :
                            key.includes('audio') ? `audio_${params.segment || 'intro'}` :
                            `avatar_${params.segment || 'intro'}`;
          const data = prod[relevantKey] || {};
          const status = key.includes('composite') ? data.composite_status :
                        key.includes('audio') ? data.status :
                        data.video_status;
          if (status === 'ready' || status === 'failed') {
            clearInterval(poll);
            setGenerating(prev => ({ ...prev, [key]: false }));
            await refreshSession();
            if (status === 'ready') toast.success(`${key} ready!`);
            else toast.error(`${key} failed`);
          }
        } catch {}
      }, 3000);
      setTimeout(() => { clearInterval(poll); setGenerating(prev => ({ ...prev, [key]: false })); }, key.includes('video') ? 660000 : 300000);
    } catch (err) {
      toast.error(err.response?.data?.detail || `${key} failed`);
      setGenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAutoSelect = async (type) => {
    const keyMap = { wow: 'wow-auto' };
    const urlMap = { wow: 'production/auto-select-wow' };
    const msgMap = { wow: 'WOW shots selected by AI' };
    const key = keyMap[type];
    setGenerating(prev => ({ ...prev, [key]: true }));
    try {
      await api.post(`/sessions/${session.id}/${urlMap[type]}`);
      await refreshSession();
      toast.success(msgMap[type]);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to auto-select ${type}`);
    }
    setGenerating(prev => ({ ...prev, [key]: false }));
  };

  const openClipPicker = async (target) => {
    setClipPickerTarget(target);
    setLoadingClips(true);
    try {
      const res = await api.get(`/sessions/${session.id}/production/available-clips`);
      setAvailableClips(res.data);
    } catch {
      toast.error("Failed to load available clips");
      setAvailableClips([]);
    }
    setLoadingClips(false);
  };

  const handlePickClip = async (clip) => {
    if (!clipPickerTarget) return;
    try {
      if (clipPickerTarget.startsWith('wow-')) {
        const idx = parseInt(clipPickerTarget.split('-')[1]);
        const newWow = [...wowShots];
        newWow[idx] = { shot_id: clip.shot_id, candidate_id: clip.candidate_id };
        await api.put(`/sessions/${session.id}/production/wow-shots`, newWow);
        toast.success(`WOW shot #${idx + 1} updated`);
      }
      await refreshSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update selection");
    }
    setClipPickerTarget(null);
  };

  const handleGenerateComposite = async (backgroundIndex = -1) => {
    setGenerating(prev => ({ ...prev, 'composite': true }));
    try {
      await api.post(`/sessions/${session.id}/generate-avatar-composite?background_index=${backgroundIndex}`);
      toast.success("Generating agent composite...");
      const poll = setInterval(async () => {
        try {
          const updated = await api.get(`/sessions/${session.id}/production`);
          const st = updated.data?.agent_composite?.status;
          if (st === 'ready' || st === 'failed') {
            clearInterval(poll);
            setGenerating(prev => ({ ...prev, 'composite': false }));
            await refreshSession();
            if (st === 'ready') toast.success("Agent composite ready!");
            else toast.error("Composite generation failed. You can retry.");
          }
        } catch {}
      }, 3000);
      setTimeout(() => { clearInterval(poll); setGenerating(prev => ({ ...prev, 'composite': false })); }, 300000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate composite");
      setGenerating(prev => ({ ...prev, 'composite': false }));
    }
  };

  const handleGenerateAll = async () => {
    setAutoRunning(true);
    try {
      await api.post(`/sessions/${session.id}/production/generate-all`);
      toast.success("Auto-generating all production assets...");
      const poll = setInterval(async () => {
        try {
          const updated = await api.get(`/sessions/${session.id}/production`);
          const autoSt = updated.data?.auto_status;
          await refreshSession();
          if (autoSt === 'done' || autoSt === 'failed') {
            clearInterval(poll);
            setAutoRunning(false);
            if (autoSt === 'done') toast.success("All production assets generated!");
            else toast.error("Some production tasks failed. Check individual sections.");
          }
        } catch {}
      }, 4000);
      setTimeout(() => { clearInterval(poll); setAutoRunning(false); }, 600000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start auto-generation");
      setAutoRunning(false);
    }
  };

  const autoProgress = production.auto_progress;
  const anyGenerating = autoRunning || Object.values(generating).some(v => v);

  return (
    <ScrollArea className="h-full">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="text-xl font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Production Resources</h2>
            <p className="text-sm text-[#676970] mt-1">Generate and preview all video assets before final assembly</p>
          </div>
          <Button onClick={handleGenerateAll} disabled={autoRunning || anyGenerating}
            data-testid="generate-all-production-btn"
            className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white text-sm shrink-0">
            {autoRunning ? <><Loader2 size={14} className="mr-2 animate-spin" /> Auto-Generating...</> :
             <><Zap size={14} className="mr-2" /> Generate All</>}
          </Button>
        </div>

        {autoRunning && autoProgress && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-[#4D11F6]/5 border border-[#4D11F6]/20" data-testid="auto-progress-banner">
            <Loader2 size={16} className="text-[#4D11F6] animate-spin shrink-0" />
            <p className="text-sm text-[#4D11F6] font-medium">{autoProgress}</p>
          </div>
        )}

        <AgentCompositeSection
          agentComposite={agentComposite}
          images={images}
          generating={generating['composite']}
          onGenerate={handleGenerateComposite}
          onGenerateWithBg={handleGenerateComposite}
        />

        <AvatarSection
          segment="intro"
          title="Avatar Intro"
          description="Intro voiceover + talking avatar video"
          script={storyboard.avatar_intro?.script}
          audioData={introAudio}
          videoData={introData}
          compositeReady={compositeReady}
          audioReady={introAudio.status === 'ready'}
          generating={{ audio: generating['audio-intro'], video: generating['video-intro'] }}
          onGenerateAudio={() => handleGenerate('generate-audio', 'audio-intro', { segment: 'intro' })}
          onGenerateVideo={() => handleGenerate('generate-avatar-video', 'video-intro', { segment: 'intro' })}
        />

        <WowShotsSection
          session={session}
          wowShots={wowShots}
          generating={generating['wow-auto']}
          onAutoSelect={() => handleAutoSelect('wow')}
          onOpenClipPicker={openClipPicker}
        />

        <WalkthroughAudioSection
          session={session}
          storyboard={storyboard}
          walkthroughAudio={walkthroughAudio}
          alignment={production.walkthrough_alignment}
          generating={generating['audio-walkthrough']}
          onGenerate={() => handleGenerate('generate-audio', 'audio-walkthrough', { segment: 'walkthrough' })}
          onRefresh={refreshSession}
        />

        <AvatarSection
          segment="cta"
          title="Avatar CTA"
          description="Closing message with call-to-action"
          script={storyboard.avatar_cta?.script}
          audioData={ctaAudio}
          videoData={ctaData}
          compositeReady={compositeReady}
          audioReady={ctaAudio.status === 'ready'}
          generating={{ audio: generating['audio-cta'], video: generating['video-cta'] }}
          onGenerateAudio={() => handleGenerate('generate-audio', 'audio-cta', { segment: 'cta' })}
          onGenerateVideo={() => handleGenerate('generate-avatar-video', 'video-cta', { segment: 'cta' })}
        />

        <ClosingFrameSection
          session={session}
        />
      </div>

      <ClipPickerDialog
        open={!!clipPickerTarget}
        onClose={() => setClipPickerTarget(null)}
        loading={loadingClips}
        clips={availableClips}
        clipPickerTarget={clipPickerTarget}
        wowShots={wowShots}
        onPickClip={handlePickClip}
      />
    </ScrollArea>
  );
}
